'use client'

import { useEffect, useState } from 'react'

/** Tope de espera a que el navegador quede libre tras `load`: si nunca hay un
 * hueco (página muy ocupada), se sigue igualmente pasado este tiempo. */
const IDLE_TIMEOUT_MS = 2000

/**
 * `true` cuando la página ha terminado de cargar (`load`) y el navegador tiene
 * un hueco libre (`requestIdleCallback`). Para diferir trabajo pesado y no
 * imprescindible para el primer pintado — p. ej. la API y el vídeo de YouTube
 * del `HeroCarousel` — sin que compita con el LCP ni con la hidratación.
 *
 * Empieza en `false` también en servidor y en la hidratación.
 */
export function useAfterPageLoad(): boolean {
  const [done, setDone] = useState(false)

  useEffect(() => {
    let idleId: number | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const scheduleIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => setDone(true), { timeout: IDLE_TIMEOUT_MS })
      } else {
        timeoutId = setTimeout(() => setDone(true), 0)
      }
    }

    if (document.readyState === 'complete') {
      scheduleIdle()
    } else {
      window.addEventListener('load', scheduleIdle, { once: true })
    }

    return () => {
      window.removeEventListener('load', scheduleIdle)
      if (idleId !== null) window.cancelIdleCallback(idleId)
      if (timeoutId !== null) clearTimeout(timeoutId)
    }
  }, [])

  return done
}
